<template>
  <v-container v-if="fund && (mayEditStepModelRequirements || awaitingAudit)" fluid>
    <v-app-bar app color="secondary" dark dense>
      <v-toolbar-title>
        {{ fund.name }}
      </v-toolbar-title>
    </v-app-bar>

    <StepModelSuccess v-if="awaitingAudit" />

    <template v-else>
      <v-row>
        <v-col cols="12">
          <h1>
            {{ fund.name }}
          </h1>
        </v-col>

        <v-col cols="12">
          <DataTable :fund="fund" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <h2 class="text-h5 mb-4">
            {{ $t("user.stepModelRequirements.title") }}
          </h2>
        </v-col>
      </v-row>

      <v-row v-for="(category, index) in categories" :key="index">
        <v-col cols="12">
          <h3>
            {{ $t(`stepModelRequirementCategory.${index}.name`) }}
          </h3>
        </v-col>

        <v-col v-for="requirement in category" :key="requirement.id" cols="12">
          <StepModelRequirementForm :fund="fund" :requirement="requirement" />
        </v-col>
      </v-row>

      <v-row v-if="!readyToFinish">
        <v-col cols="12">
          <v-alert type="error">
            {{ $t("user.stepModelRequirements.notReadyToFinish") }}
          </v-alert>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-btn color="secondary" class="mr-4" :to="{ name: 'home' }">
            {{ $t("user.stepModelRequirements.back") }}
          </v-btn>

          <v-dialog v-model="dialog" persistent max-width="600">
            <template #activator="{ on, attrs }">
              <v-btn color="primary" class="mr-4" :disabled="!readyToFinish" v-bind="attrs" v-on="on">
                {{ $t("user.stepModelRequirements.finish") }}
              </v-btn>
            </template>
            <v-card>
              <v-card-title class="headline">
                {{ $t("user.stepModelRequirements.confirmation.title") }}
              </v-card-title>
              <v-card-text>
                {{ $t("user.stepModelRequirements.confirmation.description") }}
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="warning" @click="dialog = false">
                  {{ $t("user.stepModelRequirements.confirmation.cancel") }}
                </v-btn>
                <v-btn color="primary" @click="finish" :loading="loading">
                  {{ $t("user.stepModelRequirements.confirmation.accept") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import { all, includes, filter, complement, propEq } from "ramda";
import DataTable from "@/components/Fund/DataTable";
import groupByCategory from "@/utils/Requirements/groupByCategory";
import StepModelSuccess from "@/components/User/Fund/StepModelSuccess";
import StepModelRequirementForm from "@/components/User/Fund/StepModelRequirementForm";
import mayEditStepModelRequirements from "@fng/common/mayEditStepModelRequirements";
import FundStepModelRequirementsQuery from "@/gql/queries/FundStepModelRequirementsQuery.gql";
import FinishEditStepModelRequirementsMutation from "@/gql/mutations/FinishEditStepModelRequirementsMutation.gql";

export default {
  components: {
    StepModelRequirementForm,
    StepModelSuccess,
    DataTable,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      fund: null,
      dialog: false,
      loading: false,
    };
  },

  computed: {
    categories() {
      return groupByCategory(this.fund.stepModelRequirements);
    },

    requirementsToEdit() {
      return filter(complement(propEq("state", "DONE")), this.fund.stepModelRequirements);
    },

    readyToFinish() {
      return all(
        (requirement) => !requirement.wip && (includes(requirement.state, ["DONE", "AUDIT"]) || requirement.referenced),
        this.fund.stepModelRequirements
      );
    },

    awaitingAudit() {
      return includes(this.fund.stepModelRequirementsState, [
        "AUDIT",
        "SECOND_AUDIT",
        "INTERVIEW",
        "INTERVIEW_AUDIT",
        "FINAL_AUDIT",
      ]);
    },

    mayEditStepModelRequirements() {
      return mayEditStepModelRequirements(this.fund, this.fund.auditPeriod);
    },
  },

  apollo: {
    fund: {
      query: FundStepModelRequirementsQuery,
      fetchPolicy: "cache-and-network",
      variables() {
        return {
          id: this.id,
        };
      },
    },
  },

  methods: {
    async finish() {
      this.loading = true;

      await this.$apollo.mutate({
        mutation: FinishEditStepModelRequirementsMutation,
        variables: {
          id: this.fund.id,
        },
      });

      this.dialog = false;
      this.loading = false;
    },
  },
};
</script>
